import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChatInterface } from "@/components/chat";
import { type Message } from "@/components/chat/types";
import { useConversationMessages } from "../../hooks/useConversationMessages";
import Navbar from "@/components/layout/navbar";
import { ConversationList, ConversationSwitcher, NewConversationButton } from "@/components/conversations";
import { useConversations } from "../../hooks/useConversations";

export const ChatPage: React.FC = () => {
  // Get conversation ID from route parameters
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { conversations } = useConversations();

  // Fallback to demo ID if no conversationId in route
  const actualConversationId = conversationId || "demo-conversation-id";
  
  const handleSelectConversation = (id: string) => {
    navigate(`/pa/chat/${id}`);
  };
  
  const currentConversation = conversations.find(c => c.id === actualConversationId);

  // Fetch existing messages for the conversation with enhanced features
  const {
    messages: existingMessages,
    loading: messagesLoading,
    error: messagesError,
    loadMore,
    loadPrevious,
    hasNextPage,
    hasPreviousPage,
    paginationError,
    isRetrying,
    retryCount,
    clearError,
    maxRetries,
    loadingState,
    isLoadingPrevious,
    isLoadingMore,
    loadingProgress,
    estimatedMessageCount,
  } = useConversationMessages({
    conversationId: actualConversationId,
    enabled: !!actualConversationId,
    last: 20, // Load last 20 messages by default (most recent)
    maxRetries: 3, // Allow up to 3 retry attempts
  });

  const handleMessageSent = (newMessage: Message) => {
    // In a real app, you might want to refetch messages or update cache
    console.log("Message sent:", newMessage);

    // For demo purposes, simulate an assistant response after a delay
    // Only if this is the demo conversation or test conversation
    if (actualConversationId === "demo-conversation-id" || actualConversationId.startsWith("test-conversation")) {
      setTimeout(() => {
        const assistantResponse: Message = {
          id: `response-${Date.now()}`,
          conversationId: newMessage.conversationId,
          senderRole: "AGENT",
          content: { text: "Thank you for your message! I'm here to help." },
          timestamp: new Date().toISOString(),
          metadata: {},
        };
        // Don't add to local state - let the subscription handle it
        console.log("Assistant response:", assistantResponse);
      }, 1000);
    }
  };

  // Determine error state
  const error = messagesError ? `Failed to load messages: ${messagesError.message}` : undefined;

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <Navbar />
      <ConversationSwitcher />
      <main className="flex-1 flex overflow-hidden">
        {/* Conversation Sidebar */}
        <aside className="hidden md:flex w-64 flex-col overflow-hidden">
          <ConversationList
            onSelectConversation={handleSelectConversation}
            activeConversationId={actualConversationId}
          />
        </aside>
        
        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="border-b border-border p-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {currentConversation?.title || "Untitled Conversation"}
            </h2>
            <NewConversationButton 
              variant="icon" 
              onSuccess={(conversation) => navigate(`/pa/chat/${conversation.id}`)}
            />
          </div>
          
          {/* Chat Interface */}
          <div className="flex-1 px-4 overflow-hidden">
            <ChatInterface
        conversationId={actualConversationId}
        assistantName="Personal Assistant"
        messages={existingMessages}
        onMessageSent={handleMessageSent}
        loading={messagesLoading}
        error={error}
        // Pagination props
        onLoadPrevious={loadPrevious}
        onLoadMore={loadMore}
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        loadingPrevious={isLoadingPrevious}
        loadingMore={isLoadingMore}
        // Error handling props
        paginationError={paginationError}
        isRetrying={isRetrying}
        retryCount={retryCount}
        maxRetries={maxRetries}
        onClearError={clearError}
        // Enhanced loading state props
        loadingState={loadingState}
        loadingProgress={loadingProgress}
        estimatedMessageCount={estimatedMessageCount}
      />
          </div>
        </div>
      </main>
    </div>
  );
};
